import { createClientSharedComposable, getStore } from '@global-store/store'
import type { LocationStore } from '@global-store/stores/location/types'
import { CountryCode } from '@shared/types/storefront/graphql'
import { defineStore } from 'pinia'

import { isSSR } from '@fond-of/env'
import { type Locale } from '@fond-of/locale'

export const useLocationStore = createClientSharedComposable(() =>
  defineStore('location', {
    state: (): LocationStore => ({
      currentLocation: null,
    }),

    getters: {
      location: (state) => {
        if (!state.currentLocation && isSSR()) {
          throw new Error('Location need to be set before using it.')
        }

        if (!state.currentLocation) {
          const location = new URL(globalThis.location.href)

          state.currentLocation = location
        }

        return state.currentLocation
      },
      urlPrefix(): { market: string; locale: string; urlPrefix: string } {
        const { pathname } = this.location
        const [_, urlPrefix] = pathname.split('/')

        const [locale, market] = urlPrefix.split('-')

        if (locale?.length !== 2 || market?.length !== 2) {
          return {
            market: 'eu',
            locale: 'en',
            urlPrefix: 'en-eu',
          }
        }

        return {
          market,
          locale,
          urlPrefix,
        }
      },
      // The locale in iso 2 code format (e.g. for translations)
      locale(): Locale {
        const { locale } = this.urlPrefix

        return locale as Locale
      },
      // The language in iso 2 code format uppercased (e.g. for shopify requests)
      language(): string {
        const { locale } = this.urlPrefix

        return locale.toUpperCase()
      },
      // The market short hand from the url
      market(): string {
        const { market } = this.urlPrefix

        return market
      },
      // The country in iso 2 code format uppercased (e.g. for shopify requests)
      country(): CountryCode {
        const { market } = this.urlPrefix

        if (market === 'eu') {
          return CountryCode.Ie
        }

        return market.toUpperCase() as CountryCode
      },
      // locale Codes in iso format (e.g. for Spotler language event)
      languageCode(): string {
        const languageCodes = {
          de: 'de-DE',
          en: 'en-US',
          fr: 'fr-FR',
          es: 'es-ES',
          da: 'da-DK',
          it: 'it-IT',
        }

        return languageCodes[this.locale] || languageCodes.en
      },
    },

    actions: {
      setLocation(newLocation: URL) {
        this.currentLocation = newLocation
      },
      getParams<
        TParams extends Record<string, string | string[] | undefined>,
      >(): TParams {
        return Object.fromEntries(
          this.location.searchParams.entries()
        ) as TParams
      },
    },

    hydrate: (storeState) => {
      storeState.currentLocation = new URL(globalThis.location.href)
    },
  })(getStore())
)
